import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Eye, EyeOff, Building, Clock, MapPin, Mail } from 'lucide-react';
import { toast } from 'sonner';

const SIGNIN_ERROR_RING_KEY = 'signin_error_ring';
const SIGNIN_ERROR_RING_MAX = 30;

function pushSigninErrorRing(entry) {
  try {
    let arr = [];
    try {
      const raw = localStorage.getItem(SIGNIN_ERROR_RING_KEY);
      if (typeof raw === 'string' && raw.length > 0) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) arr = parsed;
      }
    } catch (_) { /* ignore */ }
    arr.push(entry);
    if (arr.length > SIGNIN_ERROR_RING_MAX) {
      arr = arr.slice(arr.length - SIGNIN_ERROR_RING_MAX);
    }
    localStorage.setItem(SIGNIN_ERROR_RING_KEY, JSON.stringify(arr));
  } catch (_) { /* localStorage full / disabled */ }
}

const LoginPage = ({ onLogin, axiosInstance }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: Email input, 2: Password input
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  const [logoAnimationPhase, setLogoAnimationPhase] = useState('initial');
  const [backendStatus, setBackendStatus] = useState({ ok: null, message: '' });
  const [apiOverride, setApiOverride] = useState('');

  // Proactive global error monitor (ring buffer in localStorage) — captures
  // any unhandled JS errors / promise rejections while on this page, even
  // when DevTools is closed.  See the NewLoginPage monitor for full docs.
  useEffect(() => {
    let mounted = true;
    const onError = (event) => {
      if (!mounted) return;
      pushSigninErrorRing({
        t: Date.now(),
        kind: 'error',
        message: event?.message ?? null,
        filename: event?.filename ?? null,
        lineno: event?.lineno ?? null,
        colno: event?.colno ?? null,
        stack: event?.error?.stack ?? (event?.error ? String(event.error) : null),
      });
    };
    const onUnhandledRejection = (event) => {
      if (!mounted) return;
      const raw = event?.reason;
      let reason;
      if (raw instanceof Error) reason = `${raw.name}: ${raw.message}\n${raw.stack ?? ''}`;
      else if (typeof raw === 'object' && raw != null) { try { reason = JSON.stringify(raw); } catch { reason = String(raw); } }
      else reason = String(raw);
      pushSigninErrorRing({ t: Date.now(), kind: 'unhandledrejection', reason });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      mounted = false;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  // Animation sequence
  useEffect(() => {
    const animationSequence = async () => {
      // Phase 1: Logo appears with scale and fade
      setTimeout(() => setLogoAnimationPhase('fadeIn'), 100);
      
      // Phase 2: Logo pulse and glow
      setTimeout(() => setLogoAnimationPhase('pulse'), 1000);
      
      // Phase 3: Logo shrinks and moves up
      setTimeout(() => setLogoAnimationPhase('shrinkUp'), 2500);
      
      // Phase 4: Show login form
      setTimeout(() => {
        setShowAnimation(false);
      }, 3500);
    };

    animationSequence();
  }, []);

  /**
   * Intentionally no `/health` network check on component mount.
   *
   * The previous design called `axiosInstance.get('/health')` from a mount
   * useEffect.  Combined with React 18 StrictMode (double-invoke) that would
   * produce 2 spurious requests to origin BEFORE the user typed anything.
   * When the backend is DOWN / restarting / recovering from a server.py
   * import crash, Cloudflare returns HTTP 502 Bad Gateway for each of those
   * requests, and Chrome logs every 502 as a BRIGHT RED network-level line
   * in the Console tab — even when the JS .catch() fully swallows it.  The
   * user then opens a bug report about "red errors appearing on the sign-in
   * page".
   *
   * Permanent fix: defer backend-status detection until the user actually
   * performs an action (clicks Sign In or submits Forgot Password).  Only
   * then do we flip the `backendStatus` state based on the real request's
   * response / transport-layer failure.  If backend is actually DOWN we
   * render "Backend Unavailable" on the NEXT render, which is exactly when
   * the user needs to see it, and never before.
   */

  // Load saved credentials on component mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('rememberedCredentials');
    if (savedCredentials) {
      try {
        const { email, password, remember } = JSON.parse(savedCredentials);
        setFormData({ email, password });
        setRememberMe(remember);
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        localStorage.removeItem('rememberedCredentials');
      }
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const base = axiosInstance.defaults.baseURL || 'unknown';
      if (window.location.protocol === 'https:' && String(base).startsWith('http://')) {
        toast.error('Mixed content: backend is http while page is https. Set REACT_APP_BACKEND_URL to an https backend.');
        setIsLoading(false);
        return;
      }
      // Explicit 30s timeout on login; prevents infinite spinner on origin issues
      // (also inherited from axiosInstance default but kept explicit for clarity).
      const response = await axiosInstance.post('/auth/login', formData, { timeout: 30000 });
      const { access_token, user } = response.data;
      
      // Any non-throwing response from /auth/login → backend is alive.
      setBackendStatus({ ok: true, message: 'Connected' });

      // Save credentials if "Remember me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedCredentials', JSON.stringify({
          email: formData.email,
          password: formData.password,
          remember: true
        }));
      } else {
        // Remove saved credentials if "Remember me" is unchecked
        localStorage.removeItem('rememberedCredentials');
      }
      
      onLogin(user, access_token);
      toast.success(`Welcome back, ${user.full_name}!`);
      
    } catch (error) {
      const status = error?.response?.status;
      const ctRaw = error?.response?.headers?.["content-type"] ??
                   error?.response?.headers?.["Content-Type"] ??
                   null;
      const ct = typeof ctRaw === "string" ? ctRaw.toLowerCase() : "";
      const isJson = ct.includes("application/json");
      const body = error?.response?.data;
      const isBackendDown =
        status == null ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (status >= 520 && status <= 530);

      // Severity-aware console logging.  Routine backend-down events (502 /
      // restart) → console.info so DevTools doesn't paint them bright red.
      if (isBackendDown) {
        console.info(
          `[old-login:backend-down] status=${
            status == null ? "transport-failure" : status
          } ct=${ct || "n/a"}. Backend is restarting or not listening — not an application bug.`,
        );
      } else if (isJson && status !== 500) {
        console.info(
          `[old-login:app-validation] status=${status} detail=${
            body?.detail ?? "n/a"
          }`,
        );
      } else {
        // JSON 500, unknown response type, unexpected throw → red console.
        console.error('Login error:', error);
      }

      if (isBackendDown) {
        const downMsg =
          status === 502
            ? 'Origin unreachable (502 Bad Gateway)'
            : status === 503
              ? 'Service unavailable (503)'
              : status === 504
                ? 'Origin timed out (504 Gateway Timeout)'
                : status == null
                  ? 'Backend unreachable'
                  : `Origin error (HTTP ${status})`;
        setBackendStatus({ ok: false, message: downMsg });
      }

      // User-facing toast: NEVER dump raw Cloudflare HTML banner body into a
      // user-visible message; use friendly, actionable strings.
      let userMsg;
      if (isBackendDown) {
        userMsg =
          status === 502
            ? "Backend servers are not responding right now (502 Bad Gateway). Please try again in 30 seconds."
            : status === 503
              ? "Backend is temporarily down for maintenance (503). Please try again shortly."
              : status === 504
                ? "Backend servers timed out (504). Please try again."
                : status == null
                  ? "Cannot reach the backend servers right now. Please check your connection or try again."
                  : "Backend returned an error. Please try again.";
      } else if (status === 401) {
        userMsg = "Incorrect email or password";
      } else if (isJson && typeof body?.detail === "string" && body.detail.length > 0) {
        userMsg = body.detail;
      } else {
        userMsg = 'Login failed. Please try again.';
      }
      toast.error(userMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsResettingPassword(true);
    
    try {
      if (resetStep === 1) {
        // Step 1: Check if email exists
        const response = await axiosInstance.post('/auth/forgot-password', { email: resetEmail });
        setBackendStatus({ ok: true, message: 'Connected' });
        if (response.data.email_exists) {
          toast.success('Email found! Please enter your new password.');
          setResetStep(2);
        }
      } else {
        // Step 2: Reset password with new password
        if (newPassword !== confirmPassword) {
          toast.error('Passwords do not match');
          setIsResettingPassword(false);
          return;
        }
        
        if (newPassword.length < 6) {
          toast.error('Password must be at least 6 characters long');
          setIsResettingPassword(false);
          return;
        }
        
        await axiosInstance.post('/auth/forgot-password', { 
          email: resetEmail, 
          new_password: newPassword 
        });
        setBackendStatus({ ok: true, message: 'Connected' });
        
        toast.success('Password reset successful! You can now login with your new password.');
        
        // Reset form and close dialog
        setShowForgotPasswordDialog(false);
        setResetEmail('');
        setNewPassword('');
        setConfirmPassword('');
        setResetStep(1);
      }
    } catch (error) {
      const status = error?.response?.status;
      const ctRaw = error?.response?.headers?.["content-type"] ??
                   error?.response?.headers?.["Content-Type"] ??
                   null;
      const ct = typeof ctRaw === "string" ? ctRaw.toLowerCase() : "";
      const isJson = ct.includes("application/json");
      const body = error?.response?.data;
      const isBackendDown =
        status == null ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (status >= 520 && status <= 530);

      if (isBackendDown) {
        console.info(
          `[old-login:forgot-pw backend-down] status=${
            status == null ? "transport-failure" : status
          } ct=${ct || "n/a"}.`,
        );
      } else if (isJson && status !== 500) {
        console.info(
          `[old-login:forgot-pw validation] status=${status} detail=${
            body?.detail ?? "n/a"
          }`,
        );
      } else {
        console.error('Password reset error:', error);
      }

      if (isBackendDown) {
        const downMsg =
          status === 502
            ? 'Origin unreachable (502 Bad Gateway)'
            : status === 503
              ? 'Service unavailable (503)'
              : status === 504
                ? 'Origin timed out (504 Gateway Timeout)'
                : status == null
                  ? 'Backend unreachable'
                  : `Origin error (HTTP ${status})`;
        setBackendStatus({ ok: false, message: downMsg });
      }

      let userMsg;
      if (isBackendDown) {
        userMsg =
          status === 502
            ? "Backend servers are not responding right now (502 Bad Gateway). Please try again in 30 seconds."
            : status === 503
              ? "Backend is temporarily down for maintenance (503). Please try again shortly."
              : status === 504
                ? "Backend servers timed out (504). Please try again."
                : status == null
                  ? "Cannot reach the backend servers right now. Please check your connection or try again."
                  : "Backend returned an error. Please try again.";
      } else if (isJson && typeof body?.detail === "string" && body.detail.length > 0) {
        userMsg = body.detail;
      } else {
        userMsg = 'Failed to reset password. Please try again.';
      }
      toast.error(userMsg);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetDialogClose = () => {
    setShowForgotPasswordDialog(false);
    setResetEmail('');
    setNewPassword('');
    setConfirmPassword('');
    setResetStep(1);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full opacity-60 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-100 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-violet-50 rounded-full opacity-40 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
      
        <div className="relative w-full max-w-md space-y-8">
        {/* Logo/Brand Section */}
        <div className="text-center fade-in">
          <div className="mx-auto mb-6 flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_hr-system-repair-1/artifacts/au3caisj_iSource%20LOGO.png"
              alt="iSOURCE Logo"
              className="h-70 w-auto mb-4 object-contain"
            />
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">iSource Support Portal (ISP)</h1>
              <p className="text-gray-600">Smart workforce management by iSOURCE</p>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <Card className="shadow-2xl fade-in card-spinning-border" style={{ animationDelay: '0.4s' }}>
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-semibold text-gray-900">Sign In</CardTitle>
            <CardDescription className="text-gray-600">
            
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="focus-ring input-rainbow-glow"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="focus-ring pr-10 input-rainbow-glow"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <Label htmlFor="remember-me" className="text-sm text-gray-700 cursor-pointer">
                  Remember my login details
                </Label>
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-2.5 btn-animate"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="loading-spinner mr-2"></div>
                    Signing In...
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>

              {/* Forgot Password Dialog */}
              <Dialog open={showForgotPasswordDialog} onOpenChange={handleResetDialogClose}>
                <DialogTrigger asChild>
                  <Button 
                    type="button" 
                    variant="link" 
                    className="text-sm text-indigo-600 hover:text-indigo-800 p-0 h-auto font-normal"
                  >
                    Forgot password?
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-indigo-600" />
                      Reset Password {resetStep === 2 && '- Step 2'}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Password reset flow</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    {resetStep === 1 ? (
                      // Step 1: Email Input
                      <div className="space-y-2">
                        <Label htmlFor="reset-email" className="text-sm font-medium text-gray-700">
                          Email Address
                        </Label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="Enter your registered email address"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          className="focus-ring"
                          required
                          disabled={isResettingPassword}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter your email to verify it's registered in our system
                        </p>
                      </div>
                    ) : (
                      // Step 2: Password Input
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                          Email <strong>{resetEmail}</strong> found! Please enter your new password.
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="new-password">New Password</Label>
                          <div className="relative">
                            <Input
                              id="new-password"
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter your new password"
                              required
                              disabled={isResettingPassword}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="confirm-password">Confirm New Password</Label>
                          <div className="relative">
                            <Input
                              id="confirm-password"
                              type={showConfirmPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Confirm your new password"
                              required
                              disabled={isResettingPassword}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          Password must be at least 6 characters long
                        </p>
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleResetDialogClose}
                        className="flex-1"
                        disabled={isResettingPassword}
                      >
                        Cancel
                      </Button>
                      {resetStep === 2 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setResetStep(1)}
                          className="flex-1"
                          disabled={isResettingPassword}
                        >
                          Back
                        </Button>
                      )}
                      <Button
                        type="submit"
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                        disabled={isResettingPassword}
                      >
                        {isResettingPassword ? (
                          <div className="flex items-center gap-2">
                            <div className="loading-spinner"></div>
                            {resetStep === 1 ? 'Checking...' : 'Resetting...'}
                          </div>
                        ) : (
                          resetStep === 1 ? 'Verify Email' : 'Reset Password'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardFooter>
          </form>
        </Card>
        
        {/* Footer */}
        <div className="text-center text-sm text-gray-500 fade-in" style={{ animationDelay: '0.6s' }}>
          <p>Powered by iSOURCE Asia Business Solutions Corporation</p>
          <p className="text-yellow-600 font-medium mt-1">© iSource ABSC 2025 All Rights Reserved</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
